/**
 * A single Class-A sub-item row (§1.6 Build 4b-2, D-123.10/15). Status circle + identity +
 * detail + a per-record "Mark <verb>" action with an optional note popover, a checked
 * (struck-through + timestamp + note) state, undo, and a "View full record →" deep link.
 * Writes go through POST …/record-action (append-only); state is passed in, derived from
 * the event stream by the parent — this component never mutates a counter.
 */
import { useState, useRef, useEffect } from "react";
import { Link } from "wouter";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Check, CheckCircle2, Circle, FileText, Loader2, Lock, Pencil } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import type { ResolvedRecordRow } from "@shared/task-bindings";
import { splitCells, planDeepLink, letterActionLabel, downloadLetter, cellText, type RecordState, type LetterState } from "./binding-helpers";
import { LetterGapDialog, type LetterGapField } from "./LetterGapDialog";
import { EditableRecordField } from "./EditableRecordField";
import { SAGE, WARM } from "@shared/brand";

const SAGE_200 = "#C9D6C1";

function fmt(at: string | null): string {
  if (!at) return "";
  const d = new Date(at);
  if (isNaN(d.getTime())) return "";
  const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }).toLowerCase();
  return `${date}, ${time}`;
}

function DetailLine({ cells }: { cells: ReturnType<typeof splitCells>["detail"] }) {
  if (cells.length === 0) return null;
  return (
    <span>
      {cells.map((c, i) => (
        <span key={c.fieldKey}>
          {i > 0 && " · "}
          {c.secureFilePointer ? (
            // Labelled so the executor knows WHAT is in the secure file (Fix 2, s123).
            <span className="inline-flex items-center gap-0.5">{c.label}: <Lock className="h-3 w-3" /> in the secure file</span>
          ) : (
            <>
              {cellText(c)}
              {c.edited && (
                // Provenance: the planner's original value stays visible on a corrected fact (D-139.4).
                <span style={{ color: WARM.taupe, fontStyle: "italic" }} data-testid={`record-prov-${c.fieldKey}`}>
                  {" "}(was {c.originalValue && c.originalValue.trim() !== "" ? c.originalValue : "blank"})
                </span>
              )}
            </>
          )}
        </span>
      ))}
    </span>
  );
}

export function RecordRow({
  clientId, taskKey, section, sectionName, categorySlug, row, state, verb, letterTemplateType, letterState,
}: {
  clientId: string;
  taskKey: string;
  section: string;
  sectionName: string;
  categorySlug: string | null;
  row: ResolvedRecordRow;
  state: RecordState | undefined;
  verb: { button: string; past: string };
  letterTemplateType?: string;
  letterState?: LetterState;
}) {
  const qc = useQueryClient();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [note, setNote] = useState("");
  const [gapFields, setGapFields] = useState<LetterGapField[] | null>(null);
  // EXECUTOR-WRITE-1 Phase 3: the edit panel + which cell (fieldKey) is open for editing.
  const [editOpen, setEditOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const editPanelRef = useRef<HTMLDivElement>(null);
  // When the edit panel opens, scroll the WHOLE panel (incl. the trailing fields) into view —
  // it expands inline low in a long scrollable page, so without this the last fields land below
  // the fold (s143 Phase 3 fold fix). Fires only on open, not on per-field editor toggles.
  useEffect(() => {
    if (editOpen) editPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [editOpen]);
  const { identity, detail } = splitCells(row.cells);
  // Editable = a real fact cell (has a fieldId, not a Cat A secure-file pointer).
  const editableCells = row.cells.filter((c) => !c.secureFilePointer && c.fieldId);
  const done = !!state?.done;

  // SINGLE-FLIGHT-PRIMITIVE-1 (s170): `record_action` is an append-only `task_events` write. The
  // rendered state is latest-wins so a duplicate is invisible in the UI — and permanent in the log.
  // One lock covers done and undone: they are the same control in two states.
  const actFlight = useSingleFlight();

  const act = useMutation({
    mutationFn: async (action: "done" | "undone") =>
      (await apiRequest("POST", `/api/executor/${clientId}/tasks/${taskKey}/record-action`, {
        section, rowId: row.rowId, action, note: action === "done" ? note.trim() || undefined : undefined,
      })).json(),
    onSuccess: () => {
      setPopoverOpen(false);
      setNote("");
      qc.invalidateQueries({ queryKey: [`/api/executor/${clientId}/tasks/${taskKey}/events`] });
    },
  });

  // §1.6 Build 4c: generate this record's letter. 422 → open the gap dialog for the missing
  // profile fields; 201 → download the PDF + refresh events (flips the row's letter state).
  const genLetter = useMutation({
    mutationFn: async (profileFields?: Record<string, string>) => {
      const res = await fetch(`/api/executor/${clientId}/tasks/${taskKey}/records/${row.rowId}/letter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(profileFields ? { profileFields } : {}),
      });
      // 422 is the INTENTIONAL gap-flow contract (s124 Finding B, accepted): the server
      // returns it to signal "collect these profile fields first", and we open the dialog.
      // It surfaces as a console error (the browser logs every non-2xx fetch) — that is
      // expected, not a bug; no pre-check endpoint by design.
      if (res.status === 422) {
        const b = await res.json();
        throw { gap: (b.missingFields ?? []) as LetterGapField[] };
      }
      if (!res.ok) throw new Error("Failed to generate letter");
      return (await res.json()) as { letterId: string; displayName: string };
    },
    onSuccess: async (data) => {
      setGapFields(null);
      await downloadLetter(clientId, data.letterId);
      qc.invalidateQueries({ queryKey: [`/api/executor/${clientId}/tasks/${taskKey}/events`] });
    },
    onError: (err: any) => { if (err?.gap) setGapFields(err.gap); },
  });

  const hasLetter = !!letterTemplateType && !!row.letterInstanceKey;
  const generated = !!letterState?.generated;

  const link = planDeepLink(categorySlug, section);
  // s126 item 7: a jointly-owned account passes by survivorship, so the executor must know
  // it behaves differently. Detect the ownership cell === "Joint" (the survivorship field,
  // NOT account_type) and mark the row with calm sage emphasis (never warning-amber).
  const isJoint = row.cells.some((c) => c.fieldKey === "ownership" && (c.value ?? "").toLowerCase().includes("joint"));

  return (
    <div
      className="relative flex items-start gap-3 px-3 py-2 rounded-md"
      style={{
        backgroundColor: isJoint && !done ? SAGE[50] : done ? SAGE[50] : "transparent",
        ...(isJoint ? { borderLeft: `2px solid ${SAGE[600]}` } : {}),
      }}
      data-testid={`record-row-${row.rowId}`}
    >
      {done
        ? <CheckCircle2 className="h-[18px] w-[18px] shrink-0 mt-0.5" style={{ color: SAGE[600] }} aria-label="Done" />
        : <Circle className="h-[18px] w-[18px] shrink-0 mt-0.5" style={{ color: SAGE_200 }} aria-label="Not done" />}

      <div className="flex-1 min-w-0">
        <div className="text-[14px] leading-snug" style={{ color: WARM.charcoal, fontWeight: 500, textDecoration: done ? "line-through" : "none", opacity: done ? 0.6 : 1 }}>
          {/* item 4: a Cat A identity is labelled too — "[field]: 🔒 in the secure file",
              never a bare "In the secure file" row. */}
          {identity?.secureFilePointer
            ? <span className="inline-flex items-center gap-0.5">{identity.label}: <Lock className="h-3 w-3" /> in the secure file</span>
            : (identity?.value ?? "Untitled")}
          {identity?.edited && !identity.secureFilePointer && (
            <span className="font-normal" style={{ color: WARM.taupe, fontStyle: "italic" }} data-testid={`record-prov-${identity.fieldKey}`}>
              {" "}(was {identity.originalValue && identity.originalValue.trim() !== "" ? identity.originalValue : "blank"})
            </span>
          )}
          {isJoint && (
            <span className="ml-2 inline-flex items-center text-[10px] font-medium rounded-full px-1.5 py-0.5 align-middle"
              style={{ backgroundColor: WARM.white, color: SAGE[600], border: `1px solid ${SAGE_200}` }}
              data-testid={`record-joint-${row.rowId}`}>
              Joint · survivorship
            </span>
          )}
        </div>
        <div className="text-[12px] leading-snug mt-0.5" style={{ color: WARM.taupe }}>
          <DetailLine cells={detail} />
          {done && state?.at && <span>{detail.length > 0 ? " · " : ""}{verb.past} {fmt(state.at)}</span>}
          {done && state?.note && <span> · “{state.note}”</span>}
        </div>
        {/* s162 (DRAWER-RENDERER-DEFECTS-1 Defect C): wouter <Link>, not a bare <a href>.
            A raw href is a FULL PAGE LOAD. The destination route is correct
            (/category/:slug is mounted for executors), but the reload remounted the whole app,
            and while clientData and the death-state gate re-resolved the executor watched
            "0% · Plan is in early stages · Loading…" for ~2s before the section they asked for
            appeared. Client-side navigation keeps the mounted tree and the warm query cache. */}
        {link && (
          <Link href={link} className="text-[12px] inline-block mt-1" style={{ color: SAGE[600] }} data-testid={`record-link-${row.rowId}`}>
            View in {sectionName} <ArrowRight className="h-3.5 w-3.5 inline-block align-[-2px]" aria-hidden="true" />
          </Link>
        )}

        {/* EXECUTOR-WRITE-1 Phase 3: click a field to correct it. Each save writes the canonical
            planner field + records provenance (shown as "was …" above once the bindings refetch). */}
        {editOpen && (
          <div ref={editPanelRef} className="mt-2 space-y-1 scroll-mt-24 scroll-mb-24" data-testid={`record-edit-panel-${row.rowId}`}>
            {editableCells.map((c) => (
              <div key={c.fieldKey}>
                {editingKey === c.fieldKey ? (
                  <EditableRecordField
                    clientId={clientId}
                    taskKey={taskKey}
                    section={section}
                    rowId={row.rowId}
                    cell={c}
                    onDone={() => setEditingKey(null)}
                  />
                ) : (
                  <button type="button" onClick={() => setEditingKey(c.fieldKey)}
                    className="w-full text-left text-[12px] rounded-md px-2 py-1 flex items-center justify-between gap-2"
                    style={{ border: `1px solid ${WARM.stone}`, color: WARM.charcoal }}
                    data-testid={`record-editcell-${c.fieldKey}`}>
                    <span className="truncate"><span style={{ color: WARM.taupe }}>{c.fieldLabel ?? c.label}:</span> {cellText(c) ?? "—"}</span>
                    <Pencil className="h-3 w-3 shrink-0" style={{ color: SAGE[600] }} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="shrink-0 flex items-center gap-2">
        {/* §1.6 Build 4c: per-record letter action, beside Mark done. Label derives from the
            template type; after generation → "Letter <Check className="h-3 w-3 inline-block align-[-1px]" aria-hidden="true" /> · Download again". */}
        {hasLetter && (
          generated ? (
            // s126 Phase 2 item 1 / item 9: a PROPER button (was an under-affordanced text
            // link). "Download again" makes re-download read as safe/idempotent.
            <button type="button"
              onClick={() => letterState?.letterId && downloadLetter(clientId, letterState.letterId)}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[12px] font-medium"
              style={{ border: `1px solid ${SAGE_200}`, color: SAGE[600], backgroundColor: SAGE[50] }}
              data-testid={`record-letter-${row.rowId}`}>
              <FileText className="h-3 w-3" /> Letter <Check className="h-3 w-3 inline-block align-[-1px]" aria-hidden="true" /> · Download again
            </button>
          ) : (
            <button type="button" onClick={() => genLetter.mutate(undefined)} disabled={genLetter.isPending}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[12px] font-medium"
              style={{ border: `1px solid ${SAGE_200}`, color: SAGE[600] }}
              data-testid={`record-letter-${row.rowId}`}>
              {genLetter.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
              {letterActionLabel(letterTemplateType!)}
            </button>
          )
        )}
        {editableCells.length > 0 && (
          <button type="button" onClick={() => { setEditOpen((o) => !o); setEditingKey(null); }}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[12px] font-medium"
            style={{ border: `1px solid ${SAGE_200}`, color: editOpen ? "#fff" : SAGE[600], backgroundColor: editOpen ? SAGE[600] : "transparent" }}
            data-testid={`record-edit-toggle-${row.rowId}`}>
            <Pencil className="h-3 w-3" /> Edit
          </button>
        )}
        {done ? (
          <button type="button" onClick={() => actFlight.runQuiet(() => act.mutateAsync("undone"))} disabled={actFlight.inFlight || act.isPending}
            className="text-[12px]" style={{ color: WARM.taupe }} data-testid={`record-undo-${row.rowId}`}>
            Undo
          </button>
        ) : (
          <button type="button" onClick={() => setPopoverOpen((o) => !o)} disabled={act.isPending}
            className="rounded-md px-2.5 py-1 text-[12px] font-medium" style={{ border: `1px solid ${SAGE_200}`, color: SAGE[600] }}
            data-testid={`record-mark-${row.rowId}`}>
            {verb.button}
          </button>
        )}

        {gapFields && (
          <LetterGapDialog
            fields={gapFields}
            onSubmit={(v) => genLetter.mutate(v)}
            onCancel={() => setGapFields(null)}
            submitting={genLetter.isPending}
          />
        )}

        {popoverOpen && !done && (
          <div className="absolute right-3 top-10 z-10 w-64 rounded-lg p-3 shadow-lg"
            style={{ backgroundColor: WARM.white, border: `1px solid ${WARM.stone}` }} data-testid={`record-popover-${row.rowId}`}>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
              placeholder="Add a note (optional)…"
              className="w-full resize-none rounded-md px-2 py-1.5 text-[13px] outline-none"
              style={{ border: `1px solid ${WARM.stone}`, color: WARM.charcoal }} data-testid={`record-note-input-${row.rowId}`} />
            <div className="flex gap-2 mt-2">
              <button type="button" onClick={() => actFlight.runQuiet(() => act.mutateAsync("done"))} disabled={actFlight.inFlight || act.isPending}
                className="rounded-md px-3 py-1 text-[12px] font-semibold text-white" style={{ backgroundColor: SAGE[600] }}
                data-testid={`record-confirm-${row.rowId}`}>
                {verb.button}
              </button>
              <button type="button" onClick={() => { setPopoverOpen(false); setNote(""); }}
                className="text-[12px]" style={{ color: WARM.taupe }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
